import { Controller } from "@hotwired/stimulus"
import lottie from "lottie-web/build/player/lottie_light"
import { prefersReducedMotion } from "../lib/motion"
import { loadTimeoutAction, loopBudgetSpent } from "../lib/lottie_playback"

export default class extends Controller {
  static targets = ["stage"]

  static values = {
    autoplay: { type: Boolean, default: true },
    fallbackSrc: String,
    loadTimeout: Number,
    loop: { type: Boolean, default: true },
    maxLoops: { type: Number, default: 0 },
    reducedMotionFrame: { type: Number, default: 48 },
    renderer: { type: String, default: "svg" },
    src: String
  }

  connect() {
    this.load()
  }

  // Reload when the source is swapped at runtime (e.g. locating -> verified).
  // Guarded so the initial value assignment (before connect) is a no-op.
  srcValueChanged() {
    if (this.animation) this.load()
  }

  disconnect() {
    this.clearLoadTimer()
    this.destroyAnimation()
  }

  // Pause the looping animation when it is scrolled offscreen OR the tab/app is backgrounded, so a resting
  // /home never pays per-frame SVG recalc for an unseen mascot. No-op for the static reduced-motion path.
  setupVisibilityPausing(reducedMotion) {
    this.teardownVisibilityPausing()
    if (reducedMotion || !this.autoplayValue) return

    this.isOnscreen = true

    if (typeof IntersectionObserver === "function") {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.isOnscreen = entries[entries.length - 1]?.isIntersecting ?? true
        this.applyPlayState()
      }, { threshold: 0.01 })
      this.intersectionObserver.observe(this.element)
    }

    this.boundVisibilityChange = () => this.applyPlayState()
    document.addEventListener("visibilitychange", this.boundVisibilityChange)
  }

  applyPlayState() {
    if (!this.animation || !this.loaded) return

    const shouldPlay = this.autoplayValue && !this.reducedMotion && !this.rested &&
      (this.isOnscreen ?? true) && !document.hidden
    if (shouldPlay) this.animation.play()
    else this.animation.pause()
  }

  teardownVisibilityPausing() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
    if (this.boundVisibilityChange) {
      document.removeEventListener("visibilitychange", this.boundVisibilityChange)
      this.boundVisibilityChange = null
    }
  }

  load() {
    if (!this.hasStageTarget || !this.hasSrcValue) return

    this.destroyAnimation()
    this.clearLoadTimer()
    this.element.classList.remove("is-loaded", "is-failed", "is-slow")
    this.stageTarget.textContent = ""
    this.loaded = false
    this.rested = false
    this.loopCount = 0

    const reducedMotion = prefersReducedMotion()
    this.reducedMotion = reducedMotion

    this.animation = lottie.loadAnimation({
      autoplay: this.autoplayValue && !reducedMotion,
      container: this.stageTarget,
      loop: this.loopValue && !reducedMotion,
      path: this.srcValue,
      renderer: this.rendererValue,
      rendererSettings: {
        hideOnTransparent: false,
        preserveAspectRatio: "xMidYMid meet",
        progressiveLoad: true
      }
    })

    this.animation.addEventListener("DOMLoaded", () => {
      this.loaded = true
      this.clearLoadTimer()
      this.element.classList.remove("is-slow")
      this.element.classList.add("is-loaded")
      // Apply the initial onscreen/visibility state — a looping SVG Lottie costs main-thread CPU every
      // frame, so it must NOT run while scrolled offscreen or while the tab/app is backgrounded (it was a
      // steady frame-budget drain competing with the map). Reduced-motion frames stay static (no loop).
      this.applyPlayState()
    })

    this.setupVisibilityPausing(reducedMotion)

    // Loop a few times, then rest on the reduced-motion frame (a still pose) instead of looping forever.
    this.animation.addEventListener("loopComplete", () => {
      this.loopCount += 1
      if (loopBudgetSpent(this.loopCount, this.maxLoopsValue)) this.rest()
    })

    this.animation.addEventListener("data_failed", () => {
      this.showFallback()
    })

    this.animation.addEventListener("error", () => {
      this.showFallback()
    })

    if (this.hasLoadTimeoutValue && this.loadTimeoutValue > 0) {
      this.loadTimer = window.setTimeout(() => {
        const action = loadTimeoutAction({ loaded: this.loaded, hasFallback: this.hasFallbackSrcValue })
        if (action === "fallback") this.showFallback()
        else if (action === "wait") this.element.classList.add("is-slow")
      }, this.loadTimeoutValue)
    }

    if (reducedMotion) {
      this.animation.addEventListener("DOMLoaded", () => {
        this.animation.goToAndStop(this.reducedMotionFrameValue, true)
      })
    }
  }

  rest() {
    if (!this.animation) return

    this.rested = true
    this.animation.loop = false
    this.animation.goToAndStop(this.reducedMotionFrameValue, true)
  }

  destroyAnimation() {
    this.teardownVisibilityPausing()
    if (!this.animation) return

    this.animation.destroy()
    this.animation = null
  }

  showFallback() {
    this.clearLoadTimer()
    this.destroyAnimation()
    this.element.classList.add("is-failed")

    if (!this.hasFallbackSrcValue) return

    const image = document.createElement("img")
    image.src = this.fallbackSrcValue
    image.alt = ""
    image.className = "h-full w-full object-contain"

    this.stageTarget.replaceChildren(image)
  }

  clearLoadTimer() {
    if (!this.loadTimer) return

    window.clearTimeout(this.loadTimer)
    this.loadTimer = null
  }
}
